/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaFileUpload, FaUserGraduate, FaChartLine, FaShieldAlt, FaUserTie, FaUsers, FaClipboardCheck, FaCheckCircle, FaChevronLeft, FaChevronRight, FaTimes, FaEnvelope, FaPhone, FaGraduationCap, FaAward, FaBook, FaTrophy, FaMedal, FaStar, FaFilePdf } from 'react-icons/fa';

import * as authService from '../services/authService';
import * as adminService from '../services/adminService';
import * as articleService from '../services/articleService';
import * as masterService from '../services/masterService';

const Home = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isLoggedIn());
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const [recentResults, setRecentResults] = useState([]);
  const navigate = useNavigate();

  // Dark elegant gradient
  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  // Check login status
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = authService.isLoggedIn();
      setIsLoggedIn(loggedIn);

      // Scroll to top when login status changes (e.g., landing on home after login)
      window.scrollTo(0, 0);
      if (document.body) document.body.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
    };

    checkLoginStatus();

    // Listen for login/logout events
    window.addEventListener('userLogin', checkLoginStatus);
    window.addEventListener('userLogout', checkLoginStatus);

    return () => {
      window.removeEventListener('userLogin', checkLoginStatus);
      window.removeEventListener('userLogout', checkLoginStatus);
    };
  }, []);

  // Load recent results
  useEffect(() => {
    loadRecentResults();
  }, []);

  const loadRecentResults = async () => {
    try {
      console.log('Fetching recent results via public API...');
      // Fetch running titles that have declared results  (no submission deadline filter)
      let rawTitles = [];
      try {
        const titlesResponse = await articleService.getPublicRunningTitles();
        if (titlesResponse && titlesResponse.success && Array.isArray(titlesResponse.data)) {
          rawTitles = titlesResponse.data;
        } else if (Array.isArray(titlesResponse)) {
          rawTitles = titlesResponse;
        } else if (titlesResponse && titlesResponse.data && Array.isArray(titlesResponse.data)) {
          rawTitles = titlesResponse.data;
        }
      } catch (err) {
        console.error('Error fetching titles:', err);
      }

      console.log('Raw Titles received:', rawTitles);
      const declaredResults = [];

      for (const rawTitle of rawTitles) {
        const titleId = rawTitle.id || rawTitle._id;
        if (!titleId) {
          console.warn('Skipping title with no ID:', rawTitle);
          continue;
        }


        try {
          console.log(`Fetching winners for title ID: ${titleId} (${rawTitle.title || rawTitle.articleTitle})`);
          // Fetch winners for each title via public service
          const winnersResponse = await articleService.getWinners(titleId);
          console.log(`Response for title ${titleId}:`, winnersResponse);

          // Flexible handling of winners response
          const winners = winnersResponse?.winners || (Array.isArray(winnersResponse) ? winnersResponse : (winnersResponse?.data || []));
          const isSuccess = winnersResponse?.success || Array.isArray(winnersResponse) || (winnersResponse?.winners && winnersResponse.winners.length > 0);

          if (isSuccess && Array.isArray(winners) && winners.length > 0) {

            // Use result_date for display and sorting, fallback to declaration/update date
            const declaredAt = rawTitle.result_date || rawTitle.resultDate || rawTitle.updated_at || rawTitle.declared_at || new Date().toISOString();

            declaredResults.push({
              titleId: titleId,
              title: rawTitle.title || rawTitle.articleTitle || 'Recent Competition',
              declaredAt: declaredAt,
              topStudents: winners.map(w => ({
                rank: w.rank,
                name: w.student_name || w.studentName || w.name || 'Unknown Student',
                university: w.university || 'N/A',
                department: w.department || 'N/A',
                average: w.mark || w.total_marks || w.totalMarks || w.score || w.average || '0',
                fileUrl: w.file_url || w.fileUrl,
                reviewers: [
                  w.reviewed_by_name || w.reviewedByName ||
                  w.reviewer_name || w.reviewerName ||
                  (typeof w.reviewer === 'object' ? (w.reviewer?.full_name || w.reviewer?.name) : w.reviewer)
                ].filter(Boolean)
              }))
            });
          }
        } catch (err) {
          // Skip silently for titles without winners
        }
      }

      console.log(`Total declared results found: ${declaredResults.length}`);

      // Sort by declared date (most recent first)
      const sortedResults = declaredResults.sort((a, b) =>
        new Date(b.declaredAt) - new Date(a.declaredAt)
      );

      setRecentResults(sortedResults);


    } catch (error) {
      console.error('Fatal error in loadRecentResults:', error);

    }
  };

  const features = [
    {
      icon: <FaFileUpload className="text-5xl text-indigo-400" />,
      title: 'Easy Submission',
      description: 'Submit your research papers, articles, and abstracts with our intuitive platform.',
    },
    {
      icon: <FaUserGraduate className="text-5xl text-indigo-400" />,
      title: 'For All Academics',
      description: 'From graduates to professors, our platform serves all academic levels.',
    },
    {
      icon: <FaChartLine className="text-5xl text-indigo-400" />,
      title: 'Track Progress',
      description: 'Monitor your submissions, reviews, and publication status in real-time.',
    },
    {
      icon: <FaShieldAlt className="text-5xl text-indigo-400" />,
      title: 'Secure & Reliable',
      description: 'Your research is protected with industry-standard security measures.',
    },
  ];

  const benefits = [
    'Fast peer review process',
    'International recognition',
    'Open access options',
    'Expert editorial support',
    'Citation tracking',
    'Author dashboard'
  ];

  // Reviewers data with full profile details
  const reviewers = [
    {
      name: 'Dr. Rajesh Kumar',
      profession: 'Professor of Computer Science',
      institution: 'IIT Delhi',
      // image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80',
      email: 'rajesh.kumar@iitd.ac.in',
      phone: '+91 98765 43210',
      education: 'Ph.D. in Computer Science from MIT, USA',
      specialization: 'Artificial Intelligence, Machine Learning, Data Science',
      experience: '15+ years in academia and research',
      publications: '120+ peer-reviewed papers',
      achievements: [
        'Best Paper Award at ICML 2020',
        'Fellow of Indian Academy of Sciences',
        'Distinguished Researcher Award 2019'
      ],
      about: 'Dr. Rajesh Kumar is a leading researcher in the field of Artificial Intelligence with expertise in deep learning and neural networks. He has supervised over 30 PhD students and contributed significantly to advancing AI research in India.'
    },
    {
      name: 'Dr. Priya Sharma',
      profession: 'Associate Professor of Physics',
      institution: 'IISc Bangalore',
      // image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80',
      email: 'priya.sharma@iisc.ac.in',
      phone: '+91 98765 43211',
      education: 'Ph.D. in Quantum Physics from Oxford University, UK',
      specialization: 'Quantum Computing, Particle Physics, Astrophysics',
      experience: '12+ years in theoretical and experimental physics',
      publications: '95+ research papers in top journals',
      achievements: [
        'Young Scientist Award by INSA',
        'Fulbright Scholar 2018',
        'Excellence in Research Award'
      ],
      about: 'Dr. Priya Sharma specializes in quantum computing and has made groundbreaking contributions to quantum algorithm development. Her research bridges theoretical physics and practical quantum computing applications.'
    },
    {
      name: 'Dr. Amit Patel',
      profession: 'Senior Researcher in AI',
      institution: 'TIFR Mumbai',
      // image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
      email: 'amit.patel@tifr.res.in',
      phone: '+91 98765 43212',
      education: 'Ph.D. in Artificial Intelligence from Stanford University, USA',
      specialization: 'Natural Language Processing, Computer Vision, Robotics',
      experience: '10+ years in AI research and development',
      publications: '80+ publications in leading AI conferences',
      achievements: [
        'Google Research Award 2021',
        'Outstanding Young Researcher Award',
        'Patent holder for 5 AI innovations'
      ],
      about: 'Dr. Amit Patel is renowned for his work in natural language processing and has developed several state-of-the-art models that are widely used in industry. He actively collaborates with tech companies to bridge research and application.'
    },
    {
      name: 'Dr. Sunita Desai',
      profession: 'Professor of Chemistry',
      institution: 'BITS Pilani',
      // image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80',
      email: 'sunita.desai@pilani.bits-pilani.ac.in',
      phone: '+91 98765 43213',
      education: 'Ph.D. in Organic Chemistry from Cambridge University, UK',
      specialization: 'Organic Synthesis, Medicinal Chemistry, Green Chemistry',
      experience: '18+ years in chemical research',
      publications: '110+ papers in renowned chemistry journals',
      achievements: [
        'CSIR Award for Excellence in Chemistry',
        'Inventor of eco-friendly synthesis methods',
        'Editorial Board Member of 3 journals'
      ],
      about: 'Dr. Sunita Desai has pioneered sustainable chemistry practices and developed novel methods for drug synthesis. Her work has significant implications for pharmaceutical industries and environmental conservation.'
    },
    {
      name: 'Dr. Vikram Singh',
      profession: 'Research Fellow in Biotechnology',
      institution: 'AIIMS New Delhi',
      // image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&q=80',
      email: 'vikram.singh@aiims.edu',
      phone: '+91 98765 43214',
      education: 'Ph.D. in Biotechnology from Johns Hopkins University, USA',
      specialization: 'Genomics, Molecular Biology, Cancer Research',
      experience: '8+ years in medical research',
      publications: '65+ papers in medical and biotech journals',
      achievements: [
        'Young Biotechnologist Award 2022',
        'Breakthrough Cancer Research Grant',
        'DST INSPIRE Faculty Award'
      ],
      about: 'Dr. Vikram Singh focuses on cancer genomics and has contributed to understanding genetic markers for early cancer detection. His research combines computational biology with clinical applications.'
    },
    {
      name: 'Dr. Meera Iyer',
      profession: 'Professor of Mathematics',
      institution: 'Chennai Mathematical Institute',
      // image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=80',
      email: 'meera.iyer@cmi.ac.in',
      phone: '+91 98765 43215',
      education: 'Ph.D. in Pure Mathematics from Princeton University, USA',
      specialization: 'Number Theory, Algebraic Geometry, Cryptography',
      experience: '14+ years in mathematical research',
      publications: '75+ papers in top mathematics journals',
      achievements: [
        'Ramanujan Prize for Young Mathematicians',
        'Infosys Prize in Mathematical Sciences',
        'Invited Speaker at ICM 2022'
      ],
      about: 'Dr. Meera Iyer is a distinguished mathematician whose work in number theory has applications in modern cryptography. She is known for her elegant proofs and contributions to algebraic geometry.'
    },
    {
      name: 'Dr. Arjun Reddy',
      profession: 'Associate Professor of Mechanical Engineering',
      institution: 'NIT Trichy',
      // image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80',
      email: 'arjun.reddy@nitt.edu',
      phone: '+91 98765 43216',
      education: 'Ph.D. in Mechanical Engineering from Georgia Tech, USA',
      specialization: 'Robotics, Automation, Manufacturing Systems',
      experience: '11+ years in engineering research',
      publications: '70+ publications in engineering conferences',
      achievements: [
        'Best Innovation Award in Robotics',
        'Industry-Academia Collaboration Award',
        'Consultant for 15+ manufacturing companies'
      ],
      about: 'Dr. Arjun Reddy specializes in advanced manufacturing and robotics. His research on automated systems has been implemented in various industries, making production processes more efficient and sustainable.'
    },
  ];

  // Auto-slide reviewers (showing 4 at a time)
  const reviewersPerSlide = 4;
  const totalSlides = Math.ceil(reviewers.length / reviewersPerSlide);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
    }, 5000); // Change every 5 seconds
    return () => clearInterval(timer);
  }, [totalSlides]);

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getCurrentReviewers = () => {
    const start = currentSlideIndex * reviewersPerSlide;
    return reviewers.slice(start, start + reviewersPerSlide);
  };

  // Handle reviewer card click
  const handleReviewerClick = (reviewer) => {
    setSelectedReviewer(reviewer);
    setShowReviewerModal(true);
  };

  const closeReviewerModal = () => {
    setShowReviewerModal(false);
    setSelectedReviewer(null);
  };

  // Get rank badge for recent results
  const getRankBadge = (rank) => {
    if (rank === 1) {
      return (
        <div className="flex items-center justify-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-full font-bold text-sm">
          <FaTrophy className="text-xs" />
          <span>1st</span>
        </div>
      );
    } else if (rank === 2) {
      return (
        <div className="flex items-center justify-center gap-1 px-3 py-1 bg-gradient-to-r from-gray-300 to-gray-400 text-white rounded-full font-bold text-sm">
          <FaMedal className="text-xs" />
          <span>2nd</span>
        </div>
      );
    } else if (rank === 3) {
      return (
        <div className="flex items-center justify-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-full font-bold text-sm">
          <FaMedal className="text-xs" />
          <span>3rd</span>
        </div>
      );
    } else {
      return (
        <div className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-bold text-sm">
          {rank}th
        </div>
      );
    }
  };

  return (
    <>
      {/* Reviewer Profile Modal */}
      {showReviewerModal && selectedReviewer && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] overflow-y-auto px-4">
          <div className="flex min-h-full items-center justify-center py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full relative animate-fade-in">
              {/* Modal Header with Image - Using dark gradient */}
              <div className="relative h-40 md:h-64 rounded-t-2xl" style={darkGradient}>
                <button
                  onClick={closeReviewerModal}
                  className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors z-10"
                >
                  <FaTimes size={20} />
                </button>
                <div className="absolute inset-0 flex items-center justify-center">
                  {selectedReviewer.image ? (
                    <img
                      src={selectedReviewer.image}
                      alt={selectedReviewer.name}
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-xl"
                    />
                  ) : (
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white/20 flex items-center justify-center border-4 border-white shadow-xl">
                      <FaUserGraduate className="text-4xl text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                {/* Name and Title */}
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedReviewer.name}</h2>
                  <p className="text-xl font-semibold mb-1 text-indigo-600">{selectedReviewer.profession}</p>
                  <p className="text-lg text-gray-600">{selectedReviewer.institution}</p>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg">
                    <FaEnvelope className="text-xl text-indigo-600" />
                    <div>
                      <p className="text-xs text-gray-500 font-semibold">Email</p>
                      <p className="text-sm text-gray-900">{selectedReviewer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg">
                    <FaPhone className="text-xl text-indigo-600" />
                    <div>
                      <p className="text-xs text-gray-500 font-semibold">Phone</p>
                      <p className="text-sm text-gray-900">{selectedReviewer.phone}</p>
                    </div>
                  </div>
                </div>

                {/* About */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FaUserGraduate className="text-indigo-600" />
                    About
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedReviewer.about}</p>
                </div>

                {/* Education & Specialization */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaGraduationCap className="text-indigo-600" />
                      Education
                    </h3>
                    <p className="text-gray-700">{selectedReviewer.education}</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <FaBook className="text-indigo-600" />
                      Specialization
                    </h3>
                    <p className="text-gray-700">{selectedReviewer.specialization}</p>
                  </div>
                </div>

                {/* Experience & Publications */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Experience</p>
                    <p className="text-lg font-bold text-gray-900">{selectedReviewer.experience}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Publications</p>
                    <p className="text-lg font-bold text-gray-900">{selectedReviewer.publications}</p>
                  </div>
                </div>

                {/* Achievements */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FaAward className="text-indigo-600" />
                    Key Achievements
                  </h3>
                  <ul className="space-y-2">
                    {selectedReviewer.achievements.map((achievement, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50">
        {/* Note: Hero Slider Section is now in the Navbar component */}

        {/* Welcome Section */}
        <div className="relative bg-gradient-to-b from-white via-slate-50 to-white py-10 sm:py-16 overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-3xl -mt-72"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-purple-100/30 rounded-full blur-3xl mr-[-100px] mb-[-100px]"></div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              {/* Accent Line */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-indigo-400"></div>
                <span className="text-indigo-500 text-sm font-semibold uppercase tracking-[0.2em]">Academic Excellence</span>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-indigo-400"></div>
              </div>

              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 mb-4 leading-tight">
                Welcome to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600">
                  Paper Portal
                </span>
              </h2>

              <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed font-light mb-6">
                Your gateway to academic publishing. Submit, manage, and track your research papers,
                articles, and abstracts — all in one place.
              </p>


            </div>
          </div>
        </div>

        {/* Features Section - Using dark gradient */}
        <div className="py-16" style={darkGradient}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center text-white mb-12">
              Why Choose Paper Portal?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 text-center border border-white/20 hover:bg-white/15"
                >
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-200">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Results Section */}
        {recentResults.length > 0 && (
          <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col items-center text-center mb-10 sm:mb-16">
                <div className="flex flex-col items-center gap-4 sm:gap-6 mb-6">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                    <FaTrophy className="text-2xl sm:text-4xl text-white" />
                  </div>
                  <h2 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 leading-tight">
                    Live Performance <span className="text-indigo-600">Tracker</span>
                  </h2>
                </div>
                <p className="text-base sm:text-xl text-slate-600 max-w-2xl mx-auto px-6 font-light">
                  Stay updated with the latest research competition results and celebrate the achievements of our top scholars.
                </p>
              </div>

              <div className="flex overflow-x-auto space-x-6 pb-8 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-indigo-200 scrollbar-track-transparent px-4 md:px-0">
                {recentResults.map((result, index) => (
                  <div
                    key={index}
                    className="w-[85vw] sm:w-[400px] lg:w-[calc((100%-3rem)/3)] shrink-0 snap-center bg-white rounded-3xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl border border-slate-200 flex flex-col min-w-[280px]"
                  >
                    {/* Result Header */}
                    <div className="p-5 sm:p-6 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 shrink-0 relative">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                      <div className="flex items-start justify-between relative z-10">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-1 tracking-tight">{result.title}</h3>
                          <p className="text-indigo-100 text-[10px] sm:text-xs uppercase tracking-wider font-semibold">
                            Declared: {new Date(result.declaredAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full border border-white/30 shrink-0 ml-2">
                          <span className="text-white text-[10px] sm:text-xs font-bold whitespace-nowrap">
                            {result.topStudents.length} Winners
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Top Students List */}
                    <div className="p-4 sm:p-6 bg-white flex-1 flex flex-col">
                      <div className="space-y-3 sm:space-y-4 flex-1">
                        {result.topStudents.slice(0, 5).map((student, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-3 sm:p-4 rounded-xl transition-colors ${student.rank === 1 ? 'bg-amber-50/50 border border-amber-100' :
                              student.rank === 2 ? 'bg-slate-50 border border-slate-100' :
                                student.rank === 3 ? 'bg-orange-50/50 border border-orange-100' :
                                  'bg-indigo-50/30 border border-indigo-100'
                              }`}
                          >
                            <div className="flex items-center gap-3 sm:gap-4 overflow-hidden">
                              <div className="flex-shrink-0 scale-90 sm:scale-100">
                                {getRankBadge(student.rank)}
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-slate-800 text-sm sm:text-base truncate">{student.name}</h4>
                                <div className="flex flex-col">
                                  <p className="text-[10px] sm:text-xs text-indigo-600 font-semibold mb-0.5">
                                    {student.department}
                                  </p>
                                  <p className="text-[10px] sm:text-xs text-slate-500 truncate">{student.university}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 ml-2">
                              <div className="text-right">
                                <div className="text-lg sm:text-2xl font-black text-slate-900">{student.average}</div>
                                <div className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Score</div>
                              </div>
                              {student.fileUrl && (
                                <a
                                  href={student.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-50 text-rose-600 rounded-lg flex items-center justify-center hover:bg-rose-100 transition-colors border border-rose-100 shadow-sm"
                                  title="View Article PDF"
                                >
                                  <FaFilePdf size={14} className="sm:hidden" />
                                  <FaFilePdf size={18} className="hidden sm:block" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Reviewers Info */}
                      <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 shrink-0">
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <FaUserGraduate className="text-green-600" />
                          Reviewed by Expert Panel
                        </h4>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          These students were evaluated by our expert reviewers including: {
                            result.topStudents[0]?.reviewers && result.topStudents[0].reviewers.length > 0
                              ? result.topStudents[0].reviewers.slice(0, 2).join(', ')
                              : 'Senior Professors'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>


            </div>
          </div>
        )}

        {/* Young India Section - Using dark gradient */}
        <div className="relative py-16 sm:py-24 overflow-hidden" style={darkGradient}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mb-32"></div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 sm:mb-20">
              <h2 className="text-4xl sm:text-6xl font-black text-white mb-6 tracking-tight">
                Young India <span className="bg-gradient-to-r from-orange-400 via-white to-green-400 bg-clip-text text-transparent">Initiative</span>
              </h2>
              <p className="text-lg sm:text-2xl text-indigo-100 max-w-3xl mx-auto font-light leading-relaxed">
                Empowering the next generation of researchers & Innovators and Postioning India as global Hub for Innovation and Research under <span className="bg-gradient-to-r from-orange-400 via-white to-green-400 bg-clip-text text-transparent font-bold">Amrit Bharat Mahotsav</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-20 items-center">
              <div className="text-center md:text-left space-y-8">
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">About Young India</h3>
                  <p className="text-base sm:text-lg text-indigo-100/90 leading-relaxed font-light">
                    Young India is our flagship initiative dedicated to supporting graduate students,
                    post-graduates, and PhD scholars across the nation. We believe that India's future
                    lies in the hands of its young researchers and innovators.
                  </p>
                </div>
                <div>
                  <p className="text-base sm:text-lg text-indigo-100/90 leading-relaxed font-light">
                    Through this program, we provide a platform for emerging talent to publish their
                    research, access exclusive resources, connect with mentors, and contribute to India's
                    growing knowledge economy.
                  </p>
                </div>
              </div>
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt="Young India Students"
                  className="relative rounded-2xl shadow-2xl border border-white/10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                Your Path to <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Publication</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                A streamlined, three-step process designed for modern researchers.
              </p>
            </div>

            <div className="relative">
              {/* Vertical Connection Line - Mobile Only */}
              <div className="md:hidden absolute left-4 sm:left-6 top-0 bottom-0 w-1 bg-gray-100 rounded-full">
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-16">
                {/* Step 1 */}
                <div className="relative pl-12 md:pl-0">
                  {/* Mobile Indicator */}
                  <div className="md:hidden absolute left-[-8px] sm:left-[-4px] top-6 w-10 h-10 bg-white rounded-full border-4 border-indigo-500 flex items-center justify-center z-10 shadow-lg">
                    <span className="text-indigo-600 font-bold">1</span>
                  </div>

                  <div className="group">
                    <div className="relative mb-8 md:mb-6 overflow-hidden rounded-3xl shadow-xl transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2">
                      <img
                        src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&q=80"
                        alt="Create Account"
                        className="w-full h-72 md:h-64 object-cover transform transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                        <p className="text-white text-sm font-medium">Quick sign-up with role selection</p>
                      </div>
                      <div className="hidden md:flex absolute -top-4 -right-4 w-14 h-14 bg-indigo-600 text-white rounded-2xl items-center justify-center text-2xl font-black shadow-xl border-4 border-white rotate-12 group-hover:rotate-0 transition-transform">
                        1
                      </div>
                    </div>
                    <div className="md:text-center">
                      <h3 className="text-2xl font-bold mb-3 text-gray-900 group-hover:text-indigo-600 transition-colors">Create Account</h3>
                      <p className="text-gray-600 leading-relaxed">Join our elite community of scholars. Choose your profile as a researcher, professor, or reviewer in seconds.</p>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative pl-12 md:pl-0">
                  {/* Mobile Indicator */}
                  <div className="md:hidden absolute left-[-8px] sm:left-[-4px] top-6 w-10 h-10 bg-white rounded-full border-4 border-purple-500 flex items-center justify-center z-10 shadow-lg">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>

                  <div className="group">
                    <div className="relative mb-8 md:mb-6 overflow-hidden rounded-3xl shadow-xl transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2">
                      <img
                        src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80"
                        alt="Submit Paper"
                        className="w-full h-72 md:h-64 object-cover transform transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                        <p className="text-white text-sm font-medium">Secure and intuitive upload portal</p>
                      </div>
                      <div className="hidden md:flex absolute -top-4 -right-4 w-14 h-14 bg-purple-600 text-white rounded-2xl items-center justify-center text-2xl font-black shadow-xl border-4 border-white -rotate-12 group-hover:rotate-0 transition-transform">
                        2
                      </div>
                    </div>
                    <div className="md:text-center">
                      <h3 className="text-2xl font-bold mb-3 text-gray-900 group-hover:text-purple-600 transition-colors">Submit Your Work</h3>
                      <p className="text-gray-600 leading-relaxed">Upload your research papers, abstracts, or articles. Our smart system ensures your data is safe and correctly formatted.</p>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative pl-12 md:pl-0">
                  {/* Mobile Indicator */}
                  <div className="md:hidden absolute left-[-8px] sm:left-[-4px] top-6 w-10 h-10 bg-white rounded-full border-4 border-indigo-600 flex items-center justify-center z-10 shadow-lg">
                    <span className="text-indigo-600 font-bold">3</span>
                  </div>

                  <div className="group">
                    <div className="relative mb-8 md:mb-6 overflow-hidden rounded-3xl shadow-xl transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2">
                      <img
                        src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80"
                        alt="Track & Publish"
                        className="w-full h-72 md:h-64 object-cover transform transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                        <p className="text-white text-sm font-medium">Real-time status updates</p>
                      </div>
                      <div className="hidden md:flex absolute -top-4 -right-4 w-14 h-14 bg-indigo-600 text-white rounded-2xl items-center justify-center text-2xl font-black shadow-xl border-4 border-white rotate-6 group-hover:rotate-0 transition-transform">
                        3
                      </div>
                    </div>
                    <div className="md:text-center">
                      <h3 className="text-2xl font-bold mb-3 text-gray-900 group-hover:text-indigo-600 transition-colors">Track & Publish</h3>
                      <p className="text-gray-600 leading-relaxed">Monitor your peer-review status in real-time. Once approved, your work is published and accessible to the global community.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="relative py-16 md:bg-gray-50 overflow-hidden">
          {/* Mobile Background Image & Overlay */}
          <div className="absolute inset-0 md:hidden z-0">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[2px]"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="py-8 md:py-0">
                <h2 className="text-4xl font-bold mb-8 text-white md:text-gray-900 text-center md:text-left">
                  Why Researchers Trust Us
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 bg-white/10 md:bg-white backdrop-blur-md md:backdrop-blur-none p-4 rounded-xl shadow-lg border border-white/20 md:border-gray-200 group hover:bg-white/20 md:hover:bg-gray-50 transition-all"
                    >
                      <FaCheckCircle className="text-2xl flex-shrink-0 text-emerald-400 md:text-green-600" />
                      <span className="text-lg text-white md:text-gray-900 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80"
                  alt="Research Team"
                  className="rounded-2xl shadow-2xl border-4 border-white transform hover:rotate-1 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Our Reviewers Section - Using dark gradient */}
        <div className="py-20" style={darkGradient}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Our Expert Reviewers
              </h2>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                Meet our distinguished panel of reviewers from India's premier institutions
              </p>
            </div>

            {/* Reviewer Cards Container */}
            <div className="relative">
              {/* Desktop & Tablet View - Paged Grid */}
              <div className="hidden md:block">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {getCurrentReviewers().map((reviewer, index) => (
                    <div
                      key={index}
                      onClick={() => handleReviewerClick(reviewer)}
                      className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer border border-white/20 group hover:bg-white/15"
                    >
                      {/* Reviewer Image */}
                      <div className="relative h-80 overflow-hidden bg-white/5 flex items-center justify-center">
                        {reviewer.image ? (
                          <img
                            src={reviewer.image}
                            alt={reviewer.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FaUserGraduate className="text-6xl text-white/20" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                        {/* View Profile Badge */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="bg-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg text-indigo-600">
                            View Profile
                          </span>
                        </div>
                      </div>

                      {/* Reviewer Info */}
                      <div className="p-6 text-center">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {reviewer.name}
                        </h3>
                        <p className="font-semibold mb-1 text-indigo-300">
                          {reviewer.profession}
                        </p>
                        <p className="text-sm text-gray-200">
                          {reviewer.institution}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile View - Continuous Slider */}
              <div className="md:hidden flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory scrollbar-hide px-4 mask-fade-edges">
                {reviewers.map((reviewer, index) => (
                  <div
                    key={index}
                    onClick={() => handleReviewerClick(reviewer)}
                    className="w-[280px] shrink-0 snap-center bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden border border-white/20 group animate-slide-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Reviewer Image */}
                    <div className="relative h-72 overflow-hidden bg-white/5 flex items-center justify-center">
                      {reviewer.image ? (
                        <img
                          src={reviewer.image}
                          alt={reviewer.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FaUserGraduate className="text-6xl text-white/20" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    </div>

                    {/* Reviewer Info */}
                    <div className="p-6 text-center">
                      <h3 className="text-lg font-bold text-white mb-1">
                        {reviewer.name}
                      </h3>
                      <p className="text-sm font-semibold mb-1 text-indigo-300">
                        {reviewer.profession}
                      </p>
                      <p className="text-xs text-gray-200">
                        {reviewer.institution}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows for Tablet/Desktop */}
              {totalSlides > 1 && (
                <div className="hidden md:block">
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-12 h-12 bg-white/90 rounded-full shadow-lg flex items-center justify-center text-indigo-600 hover:bg-white transition-all z-10 border-2 border-white/50"
                  >
                    <FaChevronLeft className="text-xl" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-12 h-12 bg-white/90 rounded-full shadow-lg flex items-center justify-center text-indigo-600 hover:bg-white transition-all z-10 border-2 border-white/50"
                  >
                    <FaChevronRight className="text-xl" />
                  </button>
                </div>
              )}

              {/* Dots Indicator for Desktop */}
              {totalSlides > 1 && (
                <div className="hidden md:flex justify-center gap-2 mt-8">
                  {[...Array(totalSlides)].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlideIndex(index)}
                      className={`h-3 rounded-full transition-all ${index === currentSlideIndex
                        ? 'bg-white w-8'
                        : 'bg-white/50 hover:bg-white/70 w-3'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 text-center">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 md:p-8 rounded-xl shadow-lg border border-indigo-200 flex flex-col justify-center min-h-[160px]">
                <div className="text-3xl md:text-5xl font-bold text-indigo-600 mb-1 md:mb-2">1700+</div>
                <div className="text-gray-700 text-sm md:text-lg font-medium">Papers Published</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 md:p-8 rounded-xl shadow-lg border border-purple-200 flex flex-col justify-center min-h-[160px]">
                <div className="text-3xl md:text-5xl font-bold text-purple-600 mb-1 md:mb-2">2200+</div>
                <div className="text-gray-700 text-sm md:text-lg font-medium">Active Researchers</div>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 md:p-8 rounded-xl shadow-lg border border-pink-200 flex flex-col justify-center min-h-[160px]">
                <div className="text-3xl md:text-5xl font-bold text-pink-600 mb-1 md:mb-2">60+</div>
                <div className="text-gray-700 text-sm md:text-lg font-medium">Verified Reviewers</div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 md:p-8 rounded-xl shadow-lg border border-blue-200 flex flex-col justify-center min-h-[160px]">
                <div className="text-3xl md:text-5xl font-bold text-blue-600 mb-1 md:mb-2">2+</div>
                <div className="text-gray-700 text-sm md:text-lg font-medium">Countries Reached</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section - Only show if not logged in - Using dark gradient */}
        {!isLoggedIn && (
          <div className="relative py-20 overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mt-32"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl -mr-32 -mb-32"></div>

            <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-3xl sm:text-6xl font-black mb-6 text-white tracking-tight">
                Ready to Publish Your Research?
              </h2>
              <p className="text-base sm:text-2xl mb-12 text-indigo-100 font-light max-w-2xl mx-auto leading-relaxed">
                Join thousands of researchers and academics who trust Paper Portal for their high-impact publication needs.
              </p>

              <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-3 sm:gap-4 mb-10">
                {/* Featured Button on Mobile */}
                <Link
                  to="/signup?type=young_india"
                  className="col-span-2 px-6 py-4 bg-white text-indigo-900 font-bold rounded-2xl hover:bg-indigo-50 hover:shadow-2xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg"
                >
                  <FaGraduationCap className="text-xl text-indigo-600" /> Young India
                </Link>

                <Link
                  to="/signup?type=professor&role=researcher"
                  className="px-4 py-3 bg-white/10 backdrop-blur-md text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2 text-sm sm:text-base sm:px-8"
                >
                  <FaUserTie /> Researcher
                </Link>
                <Link
                  to="/signup?type=professor&role=professor"
                  className="px-4 py-3 bg-white/10 backdrop-blur-md text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2 text-sm sm:text-base sm:px-8"
                >
                  <FaBook /> Professor
                </Link>
                <Link
                  to="/signup?type=professor&role=reviewer"
                  className="px-4 py-3 bg-white/10 backdrop-blur-md text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2 text-sm sm:text-base sm:px-8"
                >
                  <FaClipboardCheck /> Reviewer
                </Link>
                <Link
                  to="/signup?type=professor&role=editorial"
                  className="px-4 py-3 bg-white/10 backdrop-blur-md text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2 text-sm sm:text-base sm:px-8"
                >
                  <FaUsers /> EB Member
                </Link>
              </div>

              <div className="flex justify-center flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="inline-block px-12 py-4 bg-transparent border-2 border-white/50 text-white text-lg font-bold rounded-full hover:bg-white hover:text-indigo-900 transition-all shadow-xl backdrop-blur-sm"
                >
                  Login Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Home;